import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonInput, IonCheckbox,
    IonSelect, IonSelectOption, IonText, IonSpinner, IonLabel, IonAlert,
} from '@ionic/react';
import { closeOutline, pencilOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { SitesResponse, UserTypesResponse } from '@/types/response/lookupResponses';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';

interface UserDetailsModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    user: UserManagementUserResponse | null;
    sites: SitesResponse[];
    userTypes: UserTypesResponse[];
    emailRegex: string;
    canFullEdit: boolean;
    onSaved: () => void;
}

interface FormData {
    userName: string;
    title: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    organization: string;
    isActive: boolean;
    isEnterpriseAdmin: boolean;
    assignedToContract: boolean;
    userTypeId: string;
    defaultSiteId: string;
}

type Mode = 'view' | 'edit';

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
    isOpen, onDidDismiss, user, sites, userTypes, emailRegex, canFullEdit, onSaved,
}) => {
    const [mode, setMode] = useState<Mode>('view');
    const [formData, setFormData] = useState<FormData | null>(null);
    const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const [showExitAlert, setShowExitAlert] = useState(false);
    const [showCancelAlert, setShowCancelAlert] = useState(false);
    const forceClosingRef = useRef(false);

    const buildForm = (u: UserManagementUserResponse): FormData => ({
        userName: u.UserName,
        title: u.Title ?? '',
        firstName: u.FirstName,
        lastName: u.LastName,
        emailAddress: u.EmailAddress,
        organization: u.Organization ?? '',
        isActive: u.IsActive,
        isEnterpriseAdmin: u.IsEnterpriseAdmin,
        assignedToContract: u.AssignedToContract,
        userTypeId: u.UserTypeId?.toString() ?? '',
        defaultSiteId: u.DefaultSiteId?.toString() ?? '',
    });

    useEffect(() => {
        if (isOpen && user) {
            forceClosingRef.current = false;
            const form = buildForm(user);
            setFormData(form);
            setOriginalFormData(form);
            setMode('view');
            setErrors({});
        }
    }, [isOpen, user]);

    const update = (field: keyof FormData, value: unknown) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : null);
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const hasChanges = () => JSON.stringify(formData) !== JSON.stringify(originalFormData);

    const handleClose = () => {
        if (forceClosingRef.current) return;
        if (mode === 'edit' && hasChanges()) setShowExitAlert(true);
        else onDidDismiss();
    };

    const handleForceClose = () => {
        setShowExitAlert(false);
        forceClosingRef.current = true;
        onDidDismiss();
    };

    const handleCancel = () => {
        if (hasChanges()) setShowCancelAlert(true);
        else { setFormData(originalFormData); setMode('view'); }
    };

    const validate = (): boolean => {
        if (!formData) return false;
        const e: Record<string, string> = {};
        if (canFullEdit) {
            if (!formData.userName.trim()) e.userName = 'User Name is required.';
            if (!formData.firstName.trim()) e.firstName = 'First Name is required.';
            if (!formData.lastName.trim()) e.lastName = 'Last Name is required.';
            if (!formData.emailAddress.trim()) {
                e.emailAddress = 'Email Address is required.';
            } else if (emailRegex && !new RegExp(emailRegex).test(formData.emailAddress)) {
                e.emailAddress = 'Email Address format is invalid.';
            }
        }
        if (!formData.isEnterpriseAdmin && formData.assignedToContract && !formData.userTypeId) {
            e.userTypeId = 'User Type is required when assigned to module.';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!formData || !user) return;
        if (!validate()) return;
        setIsSaving(true);
        try {
            if (canFullEdit) {
                await apiService.saveUserDetails({
                    Id: user.Id,
                    UserName: formData.userName.trim(),
                    Title: formData.title.trim(),
                    FirstName: formData.firstName.trim(),
                    LastName: formData.lastName.trim(),
                    EmailAddress: formData.emailAddress.trim(),
                    Organization: formData.organization.trim(),
                    IsActive: formData.isActive,
                    IsEnterpriseAdmin: formData.isEnterpriseAdmin,
                    AssignedToContract: formData.assignedToContract,
                    UserTypeId: formData.userTypeId ? parseInt(formData.userTypeId, 10) : null,
                    DefaultSiteId: formData.defaultSiteId ? parseInt(formData.defaultSiteId, 10) : null,
                });
            } else {
                await apiService.saveUserAssignment({
                    Id: user.Id,
                    AssignedToContract: formData.assignedToContract,
                    UserTypeId: formData.userTypeId ? parseInt(formData.userTypeId, 10) : null,
                });
            }
            const updated = { ...formData };
            setFormData(updated);
            setOriginalFormData(updated);
            setMode('view');
            setToast({ message: 'User details saved.', variant: 'success' });
            onSaved();
        } catch {
            setToast({ message: 'Failed to save user details.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const title = user ? `${user.FirstName} ${user.LastName} — User Details` : 'User Details';
    const showSiteSelect = formData ? (formData.isEnterpriseAdmin || formData.assignedToContract) : false;
    const showUserType = formData ? (!formData.isEnterpriseAdmin && formData.assignedToContract) : false;

    const fieldRow = (label: string, value: string) => (
        <div style={{ marginBottom: '0.75rem' }}>
            <IonLabel style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.15rem' }}>{label}</IonLabel>
            <IonText style={{ fontSize: '0.95rem' }}>{value || '—'}</IonText>
        </div>
    );

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={handleClose}
                style={{ '--width': '540px', '--height': '640px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>{title}</IonTitle>
                        <IonButtons slot="end">
                            {mode === 'view' && (
                                <IonButton onClick={() => setMode('edit')} title="Edit">
                                    <IonIcon icon={pencilOutline} />
                                </IonButton>
                            )}
                            <IonButton onClick={handleClose}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem', '--padding-bottom': '0.5rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                    ) : mode === 'view' && formData ? (
                        <div>
                            {fieldRow('User Name', formData.userName)}
                            {fieldRow('Title', formData.title)}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <div>{fieldRow('First Name', formData.firstName)}</div>
                                <div>{fieldRow('Last Name', formData.lastName)}</div>
                            </div>
                            {fieldRow('Email Address', formData.emailAddress)}
                            {fieldRow('Organization', formData.organization)}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <IonText style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>Active:</IonText>
                                    <IonText>{formData.isActive ? 'Yes' : 'No'}</IonText>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <IonText style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>Enterprise Admin:</IonText>
                                    <IonText>{formData.isEnterpriseAdmin ? 'Yes' : 'No'}</IonText>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <IonText style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>Assigned:</IonText>
                                    <IonText>{formData.assignedToContract ? 'Yes' : 'No'}</IonText>
                                </div>
                            </div>
                            {user?.UserTypeName && fieldRow('User Type', user.UserTypeName)}
                            {user?.DefaultSiteId && fieldRow('Default Site', sites.find(s => s.Id === user?.DefaultSiteId)?.Name ?? '—')}
                        </div>
                    ) : formData ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {canFullEdit && (
                                <>
                                    <div>
                                        <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>User Name *</IonLabel>
                                        <IonInput value={formData.userName} onIonInput={e => update('userName', e.detail.value ?? '')}
                                            style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                        {errors.userName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.userName}</IonText>}
                                    </div>
                                    <div>
                                        <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Title</IonLabel>
                                        <IonInput value={formData.title} onIonInput={e => update('title', e.detail.value ?? '')}
                                            style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                        <div>
                                            <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>First Name *</IonLabel>
                                            <IonInput value={formData.firstName} onIonInput={e => update('firstName', e.detail.value ?? '')}
                                                style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                            {errors.firstName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.firstName}</IonText>}
                                        </div>
                                        <div>
                                            <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Last Name *</IonLabel>
                                            <IonInput value={formData.lastName} onIonInput={e => update('lastName', e.detail.value ?? '')}
                                                style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                            {errors.lastName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.lastName}</IonText>}
                                        </div>
                                    </div>
                                    <div>
                                        <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Email Address *</IonLabel>
                                        <IonInput type="email" value={formData.emailAddress} onIonInput={e => update('emailAddress', e.detail.value ?? '')}
                                            style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                        {errors.emailAddress && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.emailAddress}</IonText>}
                                    </div>
                                    <div>
                                        <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Organization</IonLabel>
                                        <IonInput value={formData.organization} onIonInput={e => update('organization', e.detail.value ?? '')}
                                            style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                        <IonCheckbox checked={formData.isActive} onIonChange={e => update('isActive', e.detail.checked)}>Is Active</IonCheckbox>
                                        <IonCheckbox checked={formData.isEnterpriseAdmin} onIonChange={e => {
                                            update('isEnterpriseAdmin', e.detail.checked);
                                            if (e.detail.checked) update('userTypeId', '');
                                        }}>Is Enterprise Admin</IonCheckbox>
                                    </div>
                                </>
                            )}
                            <IonCheckbox checked={formData.assignedToContract} onIonChange={e => update('assignedToContract', e.detail.checked)}>
                                Is Assigned to Module
                            </IonCheckbox>
                            {showUserType && (
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>User Type *</IonLabel>
                                    <IonSelect value={formData.userTypeId} onIonChange={e => update('userTypeId', e.detail.value)}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}>
                                        {userTypes.map(ut => <IonSelectOption key={ut.Id} value={ut.Id.toString()}>{ut.Name}</IonSelectOption>)}
                                    </IonSelect>
                                    {errors.userTypeId && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.userTypeId}</IonText>}
                                </div>
                            )}
                            {showSiteSelect && (
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Default Site</IonLabel>
                                    <IonSelect value={formData.defaultSiteId} onIonChange={e => update('defaultSiteId', e.detail.value)}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}>
                                        <IonSelectOption value="">None</IonSelectOption>
                                        {sites.filter(s => s.IsActive).map(s => <IonSelectOption key={s.Id} value={s.Id.toString()}>{s.Name}</IonSelectOption>)}
                                    </IonSelect>
                                </div>
                            )}
                        </div>
                    ) : null}
                </IonContent>

                {mode === 'edit' && !isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={handleCancel}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" disabled={!hasChanges()} onClick={handleSave}>Save</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <IonAlert isOpen={showExitAlert} header="Unsaved Changes" message="Close without saving?"
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowExitAlert(false) },
                    { text: 'Close', role: 'destructive', handler: handleForceClose },
                ]}
                onDidDismiss={() => setShowExitAlert(false)}
            />
            <IonAlert isOpen={showCancelAlert} header="Discard Changes" message="Discard your unsaved changes?"
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowCancelAlert(false) },
                    { text: 'Discard', role: 'destructive', handler: () => { setShowCancelAlert(false); setFormData(originalFormData); setMode('view'); } },
                ]}
                onDidDismiss={() => setShowCancelAlert(false)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default UserDetailsModal;
